import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Location from '@nmx/utils/dist/utilities/frontend/location';
import Session from '@nmx/utils/dist/utilities/frontend/session';
import { withTheme } from 'styled-components';
import {
  autoFormatPhone,
  isPhoneNumber,
} from '@nmx/utils/dist/utilities/validators';
import LeadService from '@nmx/utils/dist/services/LeadService/Frontend';
import { Container, Row } from '../../base/Grid';
import Form from '../../form/Form';
import LoadingModule from './LoadingModule';
import ThankYouModule from './ThankYouModule';
import ProgressBar from '../../base/ProgressBar';
import Colors from '../../theme/colors';
import Recaptcha from '../../form/Recaptcha';
import { googleTagManagerTrackLead } from '../../../assets/js/pixels/google_tag_manager/track_lead';
import { floodlightsTrackLead } from '../../../assets/js/pixels/floodlights/track_lead';
import { facebookTrackLead } from '../../../assets/js/pixels/facebook/track_lead';
import { bingTrackLead } from '../../../assets/js/pixels/bing/track_lead';
import pinterestTrackLead from '../../../assets/js/pixels/pinterest/track_lead';

// Inputs
import GoalSelectInput from './inputs/GoalSelectInput';
import AgeInput from './inputs/AgeInput';
import ZipInput from './inputs/ZipInput';
import IncomeRangeSelectInput from './inputs/IncomeRangeSelectInput';
import WealthRangeSelectInput from './inputs/WealthRangeSelectInput';
import FirstNameInput from './inputs/FirstNameInput';
import LastNameInput from './inputs/LastNameInput';
import GenderPronounSelectInput from './inputs/GenderPronounSelectInput';
import EmailInput from './inputs/EmailInput';
import PhoneInput from './inputs/PhoneInput';

// styles
import {
  SectionStyled,
  ButtonBackStyled,
  IconBackButtonStyled,
  TypographyBackButtonStyled,
  DivViewContainerStyled,
  DivDynamicViewStyled,
  ButtonNextStyled,
  DivNextButtonContainerStyled,
  DivInputColStyled,
  DivProgressBarContainerStyled,
} from './styles';

// Tooltips
import {
  GoalTooltip,
  AgeTooltip,
  ZipTooltip,
  IncomeRangeTooltip,
  WealthRangeTooltip,
  FirstNameTooltip,
  LastNameTooltip,
  GenderPronounTooltip,
  EmailTooltip,
  PhoneTooltip,
} from './tooltips/tooltips';

// utilities
import {
  validateCurrentInput,
  validateStep1,
  validateStep2,
  validateStep3,
  validateStep4,
  buildLeadPayload,
  initialActiveViewsObj,
  initialAccessibilityObj,
  formExperience,
  formType,
  getUserInteractionIncrementor,
  getPageLoadPath,
  getFormName,
} from './utilities/utilities';

// miscModules
import {
  headerRow,
  getScreenNumber,
  errorSubmittingMessage,
} from './MiscModules/miscModules';

export const InPageLeadFormComponent = (props) => {
  const {
    id, config, formLocation, floodlightsendto, onFormEngaged,
  } = props;

  // ADOBE VALUES
  let pageLoadPath;
  let formName;

  // REFS /////////////////////////////
  // These refs are used to target the text within the input labels
  // so that we can capture their computed width and position the tooltips dynamically.
  const goalInputLabelText = useRef();
  const ageInputLabelText = useRef();
  const zipInputLabelText = useRef();
  const incomeRangeInputLabelText = useRef();
  const wealthRangeInputLabelText = useRef();
  const firstNameInputLabelText = useRef();
  const lastNameInputLabelText = useRef();
  const genderPronounInputLabelText = useRef();
  const emailInputLabelText = useRef();
  const phoneInputLabelText = useRef();

  // React Hooks ////////////////////////////////
  // ACTIVE VIEW SETTER
  const [view, setActiveView] = useState({ initialActiveViewsObj });

  // ACCESSIBLE VIEW SETTER
  const [accessibility, setViewAccessibility] = useState({
    initialAccessibilityObj,
  });

  const [values, setValues] = useState({
    goal: '',
    age: '',
    zip: '',
    incomeRange: '',
    wealthRange: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const [errors, setErrors] = useState({
    goal: false,
    age: false,
    zip: false,
    incomeRange: false,
    wealthRange: false,
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  });

  // Error messages are set by this component's native utilities.js validators.
  const [errorMessages, setErrorMessages] = useState({
    goal: '',
    age: '',
    zip: '',
    incomeRange: '',
    wealthRange: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const [viewValidation, setViewValidation] = useState({
    step1: false,
    step2: false,
    step3: false,
    step4: false,
  });

  const [displayWealthRange, setDisplayWealthRange] = useState(false);
  const [backButtonTarget, setBackButtonTarget] = useState('step1');
  const [progressBarFillPercentage, setProgressBarFillPercentage] = useState(0);
  const [formIsValid, setFormIsValid] = useState(false);
  // Sets the position for the tooltips next to the end of each label
  const [goalInputLabelPos, setGoalInputLabelPos] = useState('');
  const [ageInputLabelPos, setAgeInputLabelPos] = useState('');
  const [zipInputLabelPos, setZipInputLabelPos] = useState('');
  const [incomeRangeInputLabelPos, setIncomeRangeInputLabelPos] = useState('');
  const [wealthRangeInputLabelPos, setWealthRangeInputLabelPos] = useState('');
  const [firstNameInputLabelPos, setFirstNameInputLabelPos] = useState('');
  const [lastNameInputLabelPos, setLastNameInputLabelPos] = useState('');
  const [genderPronounInputLabelPos, setGenderPronounInputLabelPos] = useState(
    '',
  );
  const [emailInputLabelPos, setEmailInputLabelPos] = useState('');
  const [phoneInputLabelPos, setPhoneInputLabelPos] = useState('');
  const [allowTransition, setAllowTransition] = useState(true);

  // The fading transition for the views is currently 1000 ms
  // The accessibility transition needs to occur slightly afterwards.
  const accessibilityTransitionSpeed = 1100;

  // Accepts strings of the property accessors for the view and accessibility objects
  // and sets the correct views to be active.
  const activateViewTransition = (viewStep, accessibilityStep) => {
    setActiveView({ ...initialActiveViewsObj, [viewStep]: true });
    // NOW, we want to make isAccessibleStep1: true IMMEDIATELY, and allow the previous view to still be true...
    // DELAY...then make all OTHER accessibility fields FALSE

    // persists other current states and makes the next view and previous view to true
    setViewAccessibility({ ...accessibility, [accessibilityStep]: true });
    setTimeout(() => {
      // Sets all states to false accept for the new current view
      setViewAccessibility({
        ...initialAccessibilityObj,
        [accessibilityStep]: true,
      });
    }, accessibilityTransitionSpeed);
  };

  const setView = (step) => {
    if (allowTransition) {
      switch (step) {
        case 'step1':
          activateViewTransition('isActiveViewStep1', 'isAccessibleStep1');
          break;
        case 'step2':
          activateViewTransition('isActiveViewStep2', 'isAccessibleStep2');
          break;
        case 'step3':
          activateViewTransition('isActiveViewStep3', 'isAccessibleStep3');
          break;
        case 'step4':
          activateViewTransition('isActiveViewStep4', 'isAccessibleStep4');
          break;
        case 'isSubmitting':
          activateViewTransition(
            'isActiveViewIsSubmitting',
            'isAccessibleIsSubmitting',
          );
          break;
        case 'thankYou':
          activateViewTransition('isActiveViewThankYou', 'isAccessibleThankYou');
          break;
        case 'error':
          activateViewTransition('isActiveViewError', 'isAccessibleError');
          break;
        default:
        // do nothing
      }
      setAllowTransition(false);
      setTimeout(() => {
        setAllowTransition(true);
      }, accessibilityTransitionSpeed);
    }
  };

  // EVENT HANDLERS ///////////////////////////////////////
  const handleChange = (e) => {
    // validate individual fields as they are filled out
    const currentInputIsValid = validateCurrentInput(e, values.incomeRange);
    // Set error states for inputs in real-time.
    // If a field has an error, set the boolean to display the error tooltip.
    setErrors({ ...errors, [e.target.name]: !currentInputIsValid[0] });
    // If there are errors, set the appropriate error message returned from the validators.
    setErrorMessages({
      ...errorMessages,
      [e.target.name]: currentInputIsValid[1],
    });
    // Set input values, used for display and in the payload for submission.
    setValues({
      ...values,
      [e.target.name]: e.target.value,
    });
    // Inform parent component of engagement with form
    onFormEngaged();
  };

  const handlePhoneKeyUp = (e) => {
    // try to format the phone number on keyUp
    const phoneInput = e.target.value;
    // If the formatted num ends up being valid
    if (isPhoneNumber(phoneInput)) {
      const formattedPhoneInput = autoFormatPhone(phoneInput);
      setValues({
        ...values,
        phone: formattedPhoneInput,
      });
    }
  };

  const handleReCaptchaCallback = (recaptchaReponseToken) => {
    let request = null;
    let payload = null;
    const campaignName
      = Location.getQueryParam('campName') || 'NM.com In-page lead Form';
    const agentNumber = Session.get('agentNumber') || '';

    payload = buildLeadPayload(
      values,
      agentNumber,
      campaignName,
      recaptchaReponseToken,
    );
    payload.is_recaptcha_v3 = true; // Moving this lead form to v3 recaptcha

    request = LeadService.submitLead(payload);

    try {
      request
        .then(() => {
          // Success!
          setView('thankYou');
          // Necessary to allow optimizely metrics to track lead form submissions.
          if (window.optimizely) {
            window.optimizely.push({
              type: 'event',
              eventName: 'lead_form_submit',
            });
          }
          // Pixels
          googleTagManagerTrackLead();
          floodlightsTrackLead(
            floodlightsendto,
            config.public.googleFloodlightsId,
          );
          facebookTrackLead();
          bingTrackLead();
          pinterestTrackLead();

          pageLoadPath = getPageLoadPath();
          formName = getFormName(pageLoadPath);

          // Adobe tracking
          if (typeof window !== 'undefined' && window.adobeDataLayer) {
            window.adobeDataLayer.push({
              event: 'form-complete',
              form: {
                name: formName,
                experience: formExperience,
                'form-type': formType,
                incrementor: getUserInteractionIncrementor(),
                'submission-type': 'lead',
              },
            });
          }
        })
        .catch((error) => {
          setView('error');
          console.error(error);
        });
    } catch (error) {
      setView('error');
      console.error(error);
    }
  };

  // call this function when we've validated the form and are ready to submit
  const executeRecaptchaAndSubmit = () => {
    setView('isSubmitting');
    // Wait 1100ms to allow submitting view to fully transition in before going to confirmation view
    setTimeout(() => {
      try {
        window.grecaptcha.execute();
      } catch (error) {
        // if recaptcha execute fails, show error
        setView('error');
        console.error(error);
      }
    }, accessibilityTransitionSpeed);
  };

  // call this function when we've validated the form and there's an error
  const resetRecaptcha = () => {
    try {
      if (window.grecaptcha) {
        window.grecaptcha.reset();
      }
    } catch (error) {
      // if recaptcha execute fails, show error
      setView('error');
      console.error(error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // final check for errors
    setFormIsValid(
      viewValidation.step1
        && viewValidation.step2
        && viewValidation.step3
        && viewValidation.step4,
    );

    if (formIsValid) {
      executeRecaptchaAndSubmit();
    } else {
      resetRecaptcha();
    }
  };

  // useEffect HOOKS ////////////////////////////////////
  useEffect(() => {
    setView('step1');
    // get input label width (with refs) so we can dynamically position the tooltips (with state vars)
    setGoalInputLabelPos(
      goalInputLabelText.current.getBoundingClientRect().width,
    );
    setAgeInputLabelPos(ageInputLabelText.current.getBoundingClientRect().width);
    setZipInputLabelPos(zipInputLabelText.current.getBoundingClientRect().width);
    setIncomeRangeInputLabelPos(
      incomeRangeInputLabelText.current.getBoundingClientRect().width,
    );
    setFirstNameInputLabelPos(
      firstNameInputLabelText.current.getBoundingClientRect().width,
    );
    setLastNameInputLabelPos(
      lastNameInputLabelText.current.getBoundingClientRect().width,
    );
    setGenderPronounInputLabelPos(
      genderPronounInputLabelText.current.getBoundingClientRect().width,
    );
    setEmailInputLabelPos(
      emailInputLabelText.current.getBoundingClientRect().width,
    );
    setPhoneInputLabelPos(
      phoneInputLabelText.current.getBoundingClientRect().width,
    );
    pageLoadPath = getPageLoadPath();
    formName = getFormName(pageLoadPath);

    // // Adobe tracking on load method
    if (typeof window !== 'undefined') {
      window.adobeDataLayer = window.adobeDataLayer || [];
      window.adobeDataLayer.push({
        event: 'form-load',
        form: {
          name: formName,
          experience: formExperience,
          'form-type': formType,
          incrementor: 0,
        },
      });
    }
  }, []);

  // Updates progressBar everytime the view updates
  useEffect(() => {
    if (view.isActiveViewStep1) {
      setProgressBarFillPercentage(0);
      setBackButtonTarget('');
    } else if (view.isActiveViewStep2) {
      setProgressBarFillPercentage(33);
      setBackButtonTarget('step1');
    } else if (view.isActiveViewStep3) {
      setProgressBarFillPercentage(66);
      setBackButtonTarget('step2');
    } else if (view.isActiveViewStep4) {
      setProgressBarFillPercentage(100);
      setBackButtonTarget('step3');
    } else {
      setBackButtonTarget('');
    }
  }, [view]);

  // position tooltip for wealthRange when it displays
  useEffect(() => {
    if (displayWealthRange) {
      setWealthRangeInputLabelPos(
        wealthRangeInputLabelText.current.getBoundingClientRect().width,
      );
    }
  }, [displayWealthRange]);

  // As inputs are filled out, validate each field and check if the view is valid and complete
  useEffect(() => {
    // only validate steps if they are active
    if (view.isActiveViewStep1) {
      // STEP 1: If all fields are valid, set view as valid and activate next button
      setViewValidation({
        ...viewValidation,
        step1: validateStep1(values.goal),
      });
    } else if (view.isActiveViewStep2) {
      // Display wealth range input if low incomeRange
      setDisplayWealthRange(values.incomeRange && values.incomeRange <= 39999);
      // STEP 2: If all fields are valid, set view as valid and activate next button
      setViewValidation({
        ...viewValidation,
        step2: validateStep2(
          values.age,
          values.zip,
          values.incomeRange,
          values.wealthRange,
        ),
      });
    } else if (view.isActiveViewStep3) {
      // STEP 3: If all fields are valid, set view as valid and activate next button
      setViewValidation({
        ...viewValidation,
        step3: validateStep3(values.firstName, values.lastName),
      });
    } else if (view.isActiveViewStep4) {
      // STEP 3: If all fields are valid, set view as valid and activate next button
      setViewValidation({
        ...viewValidation,
        step4: validateStep4(values.email, values.phone),
      });
      // Only activate submit button if all steps are valid
      setFormIsValid(
        viewValidation.step1
          && viewValidation.step2
          && viewValidation.step3
          && viewValidation.step4,
      );
    }
  }, [values]);

  return (
    <SectionStyled id={id} moduleName="in-page-lead-form-module" moduleVariantion="A">
      <DivProgressBarContainerStyled
        isActive={
          view.isActiveViewStep1
          || view.isActiveViewStep2
          || view.isActiveViewStep3
          || view.isActiveViewStep4
        }>
        <ProgressBar
          fillPercentage={progressBarFillPercentage}
          fillColor={'light-blue-2'}
          bgColor={'light-blue-3'}
        />
      </DivProgressBarContainerStyled>
      <Form
        id={`${id}-form`}
        legend='Find a financial advisor form'
        onSubmit={handleSubmit}>
        <DivViewContainerStyled views={view}>
          {backButtonTarget && (
            <ButtonBackStyled
              id="in-page-lead-form-back-button"
              variant="quarternary"
              onClick={() => {
                setView(backButtonTarget);
              }}>
              <IconBackButtonStyled
                className="js-in-page-lead-animated-back-button-icon"
                variant="chevron-left"
              />
              <TypographyBackButtonStyled
                component="p"
                color={Colors.neutral.white}>
                Back
              </TypographyBackButtonStyled>
            </ButtonBackStyled>
          )}
          {/* STEP 1 //////////////////////////////// */}
          <DivDynamicViewStyled
            isActive={view.isActiveViewStep1}
            aria-hidden={!view.isActiveViewStep1}
            isAccessible={accessibility.isAccessibleStep1}>
            <Container id="in-page-lead-financial-goal-info-view">
              {getScreenNumber(1)}
              {headerRow}
              <Row align="center">
                {/* FINANCIAL GOAL SELECT DROPDOWN //////////////////////////////////////////////////////////////////// */}
                <DivInputColStyled inputWidth={390}>
                  <GoalSelectInput
                    value={values.goal}
                    onChange={handleChange}
                    error={errors.goal}
                    errorMessage={errorMessages.goal}
                    ref={goalInputLabelText}
                  />
                  <GoalTooltip leftPos={goalInputLabelPos} />
                </DivInputColStyled>
                <DivNextButtonContainerStyled>
                  <ButtonNextStyled
                    id="in-page-lead-step-1-next-button"
                    variant="primary"
                    disabled={!viewValidation.step1}
                    aria-label="Proceed to step 2"
                    onClick={() => {
                      setView('step2');
                    }}>
                    Next
                  </ButtonNextStyled>
                </DivNextButtonContainerStyled>
              </Row>
            </Container>
          </DivDynamicViewStyled>
          {/* STEP 2 //////////////////////////////// */}
          <DivDynamicViewStyled
            isActive={view.isActiveViewStep2}
            aria-hidden={!view.isActiveViewStep2}
            isAccessible={accessibility.isAccessibleStep2}>
            <Container id="in-page-lead-demographic-info-view">
              {getScreenNumber(2)}
              {headerRow}
              <Row align="center">
                {/* AGE INPUT //////////////////////////////////////////////////////////////////// */}
                <DivInputColStyled inputWidth={140}>
                  <AgeInput
                    value={values.age}
                    onChange={handleChange}
                    error={errors.age}
                    errorMessage={errorMessages.age}
                    ref={ageInputLabelText}
                  />
                  <AgeTooltip leftPos={ageInputLabelPos} />
                </DivInputColStyled>
                {/* ZIP CODE INPUT //////////////////////////////////////////////////////////////////// */}
                <DivInputColStyled inputWidth={160}>
                  <ZipInput
                    value={values.zip}
                    onChange={handleChange}
                    error={errors.zip}
                    errorMessage={errorMessages.zip}
                    ref={zipInputLabelText}
                  />
                  <ZipTooltip leftPos={zipInputLabelPos} />
                </DivInputColStyled>
                {/* INCOME RANGE SELECT DROPDOWN //////////////////////////////////////////////////////////////////// */}
                <DivInputColStyled inputWidth={235}>
                  <IncomeRangeSelectInput
                    value={values.incomeRange}
                    onChange={handleChange}
                    error={errors.incomeRange}
                    errorMessage={errorMessages.incomeRange}
                    ref={incomeRangeInputLabelText}
                  />
                  <IncomeRangeTooltip leftPos={incomeRangeInputLabelPos} />
                </DivInputColStyled>
                {/* WEALTH RANGE SELECT DROPDOWN //////////////////////////////////////////////////////////////////// */}
                {displayWealthRange && (
                  <DivInputColStyled inputWidth={225}>
                    <WealthRangeSelectInput
                      value={values.wealthRange}
                      onChange={handleChange}
                      error={errors.wealthRange}
                      errorMessage={errorMessages.wealthRange}
                      ref={wealthRangeInputLabelText}
                    />
                    <WealthRangeTooltip leftPos={wealthRangeInputLabelPos} />
                  </DivInputColStyled>
                )}
                <DivNextButtonContainerStyled>
                  <ButtonNextStyled
                    id="in-page-lead-step-2-next-button"
                    variant="primary"
                    disabled={!viewValidation.step2}
                    aria-label="Proceed to step 3"
                    onClick={() => {
                      setView('step3');
                    }}>
                    Next
                  </ButtonNextStyled>
                </DivNextButtonContainerStyled>
              </Row>
            </Container>
          </DivDynamicViewStyled>
          {/* STEP 3 //////////////////////////////// */}
          <DivDynamicViewStyled
            isActive={view.isActiveViewStep3}
            aria-hidden={!view.isActiveViewStep3}
            isAccessible={accessibility.isAccessibleStep3}>
            <Container id="in-page-lead-personal-info-view">
              {getScreenNumber(3)}
              {headerRow}
              <Row align="center">
                {/* FIRST NAME INPUT //////////////////////////////////////////////////////////////////// */}
                <DivInputColStyled inputWidth={190}>
                  <FirstNameInput
                    value={values.firstName}
                    onChange={handleChange}
                    error={errors.firstName}
                    errorMessage={errorMessages.firstName}
                    ref={firstNameInputLabelText}
                  />
                  <FirstNameTooltip leftPos={firstNameInputLabelPos} />
                </DivInputColStyled>
                {/* LAST NAME INPUT //////////////////////////////////////////////////////////////////// */}
                <DivInputColStyled inputWidth={190}>
                  <LastNameInput
                    value={values.lastName}
                    onChange={handleChange}
                    error={errors.lastName}
                    errorMessage={errorMessages.lastName}
                    ref={lastNameInputLabelText}
                  />
                  <LastNameTooltip leftPos={lastNameInputLabelPos} />
                </DivInputColStyled>
                {/* GENDER PRONOUN SELECT DROPDOWN //////////////////////////////////////////////////////////////////// */}
                <DivInputColStyled inputWidth={190}>
                  <GenderPronounSelectInput
                    value={values.genderPronoun}
                    onChange={handleChange}
                    ref={genderPronounInputLabelText}
                  />
                  <GenderPronounTooltip leftPos={genderPronounInputLabelPos} />
                </DivInputColStyled>
                <DivNextButtonContainerStyled>
                  <ButtonNextStyled
                    id="in-page-lead-step-3-next-button"
                    variant="primary"
                    disabled={!viewValidation.step3}
                    aria-label="Proceed to step 4"
                    onClick={() => {
                      setView('step4');
                    }}>
                    Next
                  </ButtonNextStyled>
                </DivNextButtonContainerStyled>
              </Row>
            </Container>
          </DivDynamicViewStyled>
          {/* STEP 4 //////////////////////////////// */}
          <DivDynamicViewStyled
            isActive={view.isActiveViewStep4}
            aria-hidden={!view.isActiveViewStep4}
            isAccessible={accessibility.isAccessibleStep4}>
            <Container id="in-page-lead-contact-info-view">
              {getScreenNumber(4)}
              {headerRow}
              <Row align="center">
                {/* EMAIL INPUT //////////////////////////////////////////////////////////////////// */}
                <DivInputColStyled inputWidth={260}>
                  <EmailInput
                    value={values.email}
                    onChange={handleChange}
                    error={errors.email}
                    errorMessage={errorMessages.email}
                    ref={emailInputLabelText}
                  />
                  <EmailTooltip leftPos={emailInputLabelPos} />
                </DivInputColStyled>
                {/* PHONE INPUT //////////////////////////////////////////////////////////////////// */}
                <DivInputColStyled inputWidth={260}>
                  <PhoneInput
                    value={values.phone}
                    onChange={handleChange}
                    onKeyUp={handlePhoneKeyUp}
                    error={errors.phone}
                    errorMessage={errorMessages.phone}
                    ref={phoneInputLabelText}
                  />
                  <PhoneTooltip leftPos={phoneInputLabelPos} />
                </DivInputColStyled>
                <DivNextButtonContainerStyled>
                  <ButtonNextStyled
                    id="in-page-lead-step-submit-button"
                    variant="primary"
                    type="submit"
                    disabled={!formIsValid}
                    aria-label="Submit form">
                    Submit
                  </ButtonNextStyled>
                </DivNextButtonContainerStyled>
              </Row>
            </Container>
          </DivDynamicViewStyled>
          {/* IS SUBMITTING //////////////////////////////// */}
          <DivDynamicViewStyled
            isActive={view.isActiveViewIsSubmitting}
            aria-hidden={!view.isActiveViewIsSubmitting}
            isAccessible={accessibility.isAccessibleIsSubmitting}
            centered={true}>
            <LoadingModule />
          </DivDynamicViewStyled>
          {/* THANK YOU //////////////////////////////// */}
          <DivDynamicViewStyled
            isActive={view.isActiveViewThankYou}
            aria-hidden={!view.isActiveViewThankYou}
            isAccessible={accessibility.isAccessibleThankYou}
            centered={true}>
            <ThankYouModule userName={values.firstName} />
          </DivDynamicViewStyled>
          {/* ERROR //////////////////////////////// */}
          <DivDynamicViewStyled
            isActive={view.isActiveViewError}
            aria-hidden={!view.isActiveViewError}
            isAccessible={accessibility.isAccessibleError}
            centered={true}>
            {errorSubmittingMessage}
          </DivDynamicViewStyled>
        </DivViewContainerStyled>
      </Form>
      <Recaptcha
        sitekey={config.public.recaptchaV3}
        size="invisible"
        callback={handleReCaptchaCallback}
        formLocation={formLocation}
        values={values}
      />
    </SectionStyled>
  );
};

InPageLeadFormComponent.propTypes = {
  id: PropTypes.string.isRequired,
  formLocation: PropTypes.string.isRequired,
  config: PropTypes.shape({
    public: PropTypes.shape({
      googleFloodlightsId: PropTypes.string,
      recaptchaInvisible: PropTypes.string,
    }).isRequired,
  }),
  /** This callback is used to tell parent component that the form has first been engaged with */
  onFormEngaged: PropTypes.func,
};

InPageLeadFormComponent.defaultProps = {
  // Neccesarry to allow legacy apps to capture the configs with string replace
  config: {
    public: {
      googleFloodlightsId: '<%=googleFloodlightsId%>',
      recaptchaInvisible: '<%=recaptchaInvisible%>',
    },
  },
  onFormEngaged: () => {},
};

export default withTheme(InPageLeadFormComponent);
